import React, { useState, useEffect, useCallback } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { Button } from '@material-ui/core';

import TextField from '../components/TextField';
import FullScreenDialog from '../components/FullScreenDialog';
import DatePicker from '../components/DatePicker';
import Select from '../components/Select';
import Autocomplete from '../components/Autocomplete';

import useQueryParams from '../hooks/useQueryParams';
import useSnackbarText from '../hooks/useSnackbarText';
import useMainContext from '../hooks/useMainContext';

import { 
	prospectResourceOptions,
	prospectOutcomeOptions,
} from '../configs/prospects';
import prospects from '../api/prospects';
import { maxWidth } from '../components/configs';

const queryParamKey = 'update-prospect';

const generateDefaultState = () => {
	return {
		prospectDate: null,
		name: '',
		phone: '',
		otherContact: '',
		resource: prospectResourceOptions[0].value,
		outcome: prospectOutcomeOptions[0].value,
		remark: '',
		introducer: ''
	}
};

const useStyles = makeStyles(theme => ({
	content: {
		maxWidth,
		margin: 'auto',
		width: '100%',
		backgroundColor: '#fff',
		padding: 10,
		alignItems: 'center',
		display: 'flex',
		flexDirection: 'column'
	},
	loading: {
		marginTop: 50,
		marginBottom: 50,
		color: '#c7c7c7'
	},
	submitButton: {
		width: '100%',
		marginTop: 20,
		marginBottom: 20,
		color: '#fff',
		borderRadius: 6,
		fontWeight: 600,
		backgroundColor: theme.palette.success.main,
		'&:hover': {
			backgroundColor: theme.palette.success.dark,
		}
	}
}));

function UpdateProspectDialog(props) {
	const classes = useStyles();
	const handleSnackbarChange = useSnackbarText();
	const [queryParams, setQueryParams] = useQueryParams();
	const [state, _setState] = useState(generateDefaultState);
	const [error, setError] = useState({});
	const [disabled, setDisable] = useState(false);
	const [loaded, setLoaded] = useState(false);
	const [introducers, setIntroducers] = useState(null);
	const { onProspectClose } = useMainContext();

	const queryValue = queryParams[queryParamKey];

	const setState = useCallback((newState = {}) => {
		_setState((prevState) => ({
			...prevState,
			...newState,
		}));
	}, []);

	const handleValueCheck = () => {
		let noError = true;
		let error = {};
		if(!state.name) {
			noError = false;
			error.name = 'Invalid value.';
		}
		if(!state.phone) {
			noError = false;
			error.phone = 'Invalid value.';
		}
		setError(error);
		return noError;
	};

	const handleChange = (type) => (event, value) => {
		if(value !== undefined) setState({ [type]: value });
		else setState({ [type]: event.target.value });
	};

	const handleSubmit = async () => {
		if(!handleValueCheck()) {
			handleSnackbarChange({ text: 'Invalid inputs', variant: 'error' });
			return window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
		};

		setDisable(true);
		let { data, success } = await prospects.updateProspect({
			...state,
			prospectID: queryValue,
		});
		setDisable(false);
		if(success) {
			handleSnackbarChange({ text: 'Prospect updated', variant: 'success' });
		} else {
			return handleSnackbarChange({ text: data.message, variant: 'error' });
		}

		if(onProspectClose) onProspectClose({ ...state, prospectID: parseInt(queryValue) });
		setQueryParams({ 'update-prospect': null });
	};

	useEffect(() => {
		_setState(generateDefaultState());
	}, [queryValue]);

	useEffect(() => {
		const prospectID = queryValue;
		const getData = async () => {
			let { data, success } = await prospects.getProspect({ prospectID });
			if(success) {
				setState({
					...data,
					resource: String(data.resource),
					outcome: String(data.outcome),
				});
				setLoaded(true);
			} else {
				return handleSnackbarChange({ text: data.message, variant: 'error' });
			}
		}

		if(prospectID) getData();
	}, [queryValue, handleSnackbarChange, setState, setLoaded]);

	// get introducers
	useEffect(() => {
		(async () => {
			let { data, success } = await prospects.getProspectIntroducers();
			setIntroducers(success ? data.introducers : []);
		})();
	}, []);

	return (
		<FullScreenDialog 
			title="Update Prospect"
			queryParamKey={queryParamKey}
		>
			{!loaded && <div className={classes.loading}>loading</div>}
			{loaded && (
				<div className={classes.content}>
					<DatePicker
						label="Date"
						value={state.prospectDate}
						onChange={handleChange('prospectDate')}
					/>

					<TextField 
						label="Name*"
						value={state.name}
						onChange={handleChange('name')}
						error={Boolean(error.name)}
						helperText={error.name}
					/>

					<TextField 
						label="Phone*"
						value={state.phone}
						onChange={handleChange('phone')}
						error={Boolean(error.phone)}
						helperText={error.phone}
						type="tel"
					/>

					<TextField 
						label="Other Contact"
						value={state.otherContact}
						onChange={handleChange('otherContact')}
					/>

					<Select 
						label="Resource"
						options={prospectResourceOptions}
						value={state.resource}
						onChange={handleChange('resource')}
					/>

					<Select 
						label="Outcome"
						options={prospectOutcomeOptions}
						value={state.outcome}
						onChange={handleChange('outcome')}
					/>

					<TextField 
						label="Remarks"
						value={state.remark}
						onChange={handleChange('remark')}
					/>

					<Autocomplete 
						label="Introducer"
						value={state.introducer}
						onChange={handleChange('introducer')}
						loading={introducers === null}
						options={introducers || []}
					/>

					<Button
						className={classes.submitButton}
						disabled={disabled}
						variant="contained"
						color="primary"
						onClick={handleSubmit}
					>
						Update
					</Button>
				</div>
			)}
		</FullScreenDialog>
	);
}

export default UpdateProspectDialog;
